import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { controlCenterApi } from '@/lib/api'
import StatusBadge from '@/components/StatusBadge'
import PageHeader from '@/components/PageHeader'

type Service = {
  service_key?: string
  key?: string
  name?: string
  service_group?: string
  group?: string
  status?: string
  port?: number | string
  health?: string | null
  capabilities?: string[]
  depends?: string[]
  shell_integration?: string
}

type ServicesResponse = { success?: boolean; services?: Service[] }

export default function Services() {
  const { data, isLoading, isError } = useQuery<ServicesResponse>({
    queryKey: ['platform', 'services'],
    queryFn: controlCenterApi.listServices,
  })

  const [groupFilter, setGroupFilter] = useState<string>('all')

  const services = data?.services ?? []
  const groups = useMemo(() => {
    const set = new Set<string>()
    for (const s of services) set.add(String(s.service_group || s.group || 'ungrouped'))
    return Array.from(set).sort()
  }, [services])

  const filtered = services.filter((s) => {
    if (groupFilter === 'all') return true
    return String(s.service_group || s.group) === groupFilter
  })

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Services"
        description={`${services.length} services in the registry. Source: services.yaml.`}
        actions={
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-sm"
          >
            <option value="all">All groups</option>
            {groups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        }
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load services. Confirm aios-backend is running and you're authenticated.
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Group</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Port</th>
              <th className="px-4 py-2.5 font-medium">Health</th>
              <th className="px-4 py-2.5 font-medium">Depends on</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  No services match the current filter.
                </td>
              </tr>
            ) : (
              filtered.map((s) => {
                const id = s.service_key || s.key || s.name || ''
                return (
                  <tr key={id} className="border-t border-border">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{s.name || id}</div>
                      <div className="text-xs text-muted-foreground">{id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {s.service_group || s.group || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{s.port ?? '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                      {s.health || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {Array.isArray(s.depends) && s.depends.length > 0
                        ? s.depends.join(', ')
                        : '—'}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
